/**
 * MessageSenderChip — "who sent this" in the bottom meta row of every message.
 *
 * Owner directive (2026-07-24): a thread is a place where several people talk,
 * not a back-and-forth with one assistant, so every message says who sent it —
 * Prest0n included — right next to the other message details (time, compute,
 * copy, ⓘ). Clicking the name opens a pop-out identity card, the same
 * interaction as the usage ⓘ.
 *
 * Presentational + self-contained: identity is resolved upstream by the pure
 * `resolveMessageSender` (services/groupThreads/senderIdentity), and the card is
 * body-portaled with the same viewport-aware placement the usage popup uses, so
 * it is never clipped by the chat scroll container.
 */
import React from 'react';
import ReactDOM from 'react-dom';
import type { MessageSenderIdentity } from '../../services/groupThreads/senderIdentity';
import { BODY_PORTAL_Z_INDEX } from '../../styles/layers';

export interface MessageSenderChipProps {
  identity: MessageSenderIdentity;
  /** Card open state is owned by the message list (one card at a time). */
  isActive: boolean;
  onToggle: () => void;
}

const KIND_GLYPH_BG: Record<MessageSenderIdentity['kind'], string> = {
  // Prest0n keeps the ink-dark glyph; humans get the gold identity chip.
  assistant: 'linear-gradient(135deg, var(--brand-charcoal) 0%, #1b1b1b 100%)',
  participant: 'linear-gradient(135deg, var(--brand-gold) 0%, var(--brand-gold-hover) 100%)',
  self: 'linear-gradient(135deg, var(--brand-gold) 0%, var(--brand-gold-hover) 100%)',
};

export const MessageSenderChip: React.FC<MessageSenderChipProps> = ({ identity, isActive, onToggle }) => {
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const cardRef = React.useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = React.useState<{ top: number; left: number } | null>(null);

  // Viewport-aware placement (mirrors the usage popup): prefer above, flip below
  // when it doesn't fit, and clamp inside the viewport on both axes.
  React.useLayoutEffect(() => {
    if (!isActive) {
      setPlacement(null);
      return;
    }
    const anchor = buttonRef.current;
    const card = cardRef.current;
    if (!anchor || !card) return;
    const a = anchor.getBoundingClientRect();
    const c = card.getBoundingClientRect();
    const gap = 10;
    const pad = 12;
    const fitsAbove = c.height + gap + pad <= a.top;
    let top = fitsAbove ? a.top - gap - c.height : a.bottom + gap;
    top = Math.max(pad, Math.min(top, window.innerHeight - c.height - pad));
    let left = a.left + a.width / 2 - c.width / 2;
    left = Math.max(pad, Math.min(left, window.innerWidth - c.width - pad));
    setPlacement({ top, left });
  }, [isActive]);

  // Dismiss on outside click / Escape. The card is portaled to <body>, so the
  // check must exempt both the card and the chip itself.
  React.useEffect(() => {
    if (!isActive) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (cardRef.current?.contains(t) || buttonRef.current?.contains(t)) return;
      onToggle();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onToggle();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [isActive, onToggle]);

  const card = isActive
    ? ReactDOM.createPortal(
        <div
          ref={cardRef}
          role="dialog"
          data-prest0n-portal="true"
          aria-label={`About ${identity.card.title}`}
          style={{
            position: 'fixed',
            top: placement?.top ?? -9999,
            left: placement?.left ?? -9999,
            // Hidden until measured so the first paint never flashes at 0,0.
            visibility: placement ? 'visible' : 'hidden',
            // The chat's own portal rung — a bespoke near-int32-max number
            // here outranked the file preview and the sign-in surface, which
            // both belong above a chat popover (2026-09-11).
            zIndex: BODY_PORTAL_Z_INDEX,
            minWidth: '190px',
            maxWidth: '280px',
            padding: '12px 14px',
            borderRadius: '12px',
            background: '#fff',
            border: '1px solid rgba(198,167,94,0.35)',
            boxShadow: '0 12px 32px rgba(0,0,0,0.16)',
            fontFamily: "'Inter',-apple-system,BlinkMacSystemFont,sans-serif",
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
            <span
              aria-hidden="true"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                background: KIND_GLYPH_BG[identity.kind],
                color: '#fff',
                fontSize: '0.8rem',
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {identity.initial}
            </span>
            <span style={{ minWidth: 0 }}>
              <span
                style={{
                  display: 'block',
                  fontSize: '0.86rem',
                  fontWeight: 700,
                  color: '#22201c',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {identity.card.title}
              </span>
              {identity.card.subtitle && (
                <span style={{ display: 'block', fontSize: '0.72rem', color: '#8a8578' }}>
                  {identity.card.subtitle}
                </span>
              )}
            </span>
          </div>
          {identity.card.rows.length > 0 && (
            <div style={{ marginTop: '10px', display: 'grid', gap: '4px' }}>
              {identity.card.rows.map((row) => (
                <div
                  key={`${row.label}-${row.value}`}
                  style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '0.73rem' }}
                >
                  <span style={{ color: '#8a8578' }}>{row.label}</span>
                  <span
                    style={{
                      color: '#3a3733',
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.value}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        // T1 (2026-09-10): a delegated post's provenance is in the accessible
        // name too, not hover alone — a screen-reader user must not be the one
        // reader who cannot discover it.
        aria-label={
          identity.delegation
            ? `Sent by ${identity.card.title}. ${identity.delegation.detail}`
            : `Sent by ${identity.card.title}`
        }
        aria-expanded={isActive}
        title={
          identity.delegation
            ? `Sent by ${identity.card.title} — ${identity.delegation.detail}`
            : `Sent by ${identity.card.title}`
        }
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '5px',
          margin: 0,
          padding: '1px 2px',
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          font: 'inherit',
          lineHeight: 1,
          color: identity.kind === 'assistant' ? '#5f5a52' : '#8a6d2f',
          // Meta-row nowrap contract (Miles 2026-08-01): the bubble's
          // inherited word-break: break-word let the docked meta row break
          // "Prest0n" MID-NAME. The chip never shrinks and never breaks a
          // name, at any width — the timestamp's docked density rule absorbs
          // narrow panes instead (formatMessageMetaTimestamp).
          whiteSpace: 'nowrap',
          wordBreak: 'normal',
          flexShrink: 0,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '15px',
            height: '15px',
            borderRadius: '50%',
            background: KIND_GLYPH_BG[identity.kind],
            color: '#fff',
            fontSize: '0.55rem',
            fontWeight: 700,
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          {identity.initial}
        </span>
        {/* whiteSpace nowrap ON THE LABEL TOO (2026-08-01): belt-and-
            suspenders with the button above — no mid-word break of a sender
            name is possible anywhere, at any width. */}
        <span style={{ fontWeight: 700, letterSpacing: '0.01em', whiteSpace: 'nowrap' }}>
          {identity.label}
        </span>
        {/* THE PERMANENT MARK (T-R8, 2026-09-10). A post Prest0n composed at
            this person's instruction wears their name and avatar — and this
            small star, his own glyph, saying his hand is in it. Quiet at rest,
            the whole sentence on hover, shown to EVERYONE including the person
            who asked for it. It is derived from the message itself, so it
            cannot be turned off, styled away, or lost on reload. */}
        {identity.delegation && (
          <span
            title={identity.delegation.detail}
            data-prest0n-delegated="true"
            style={{
              fontSize: '0.6rem',
              lineHeight: 1,
              opacity: 0.72,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {identity.delegation.glyph}
          </span>
        )}
      </button>
      {card}
    </>
  );
};

export default MessageSenderChip;
